import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Menu, Bell, ChevronDown, LogOut } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useNotificationUnreadCount } from "@/hooks/useNotifications";
import GlobalSearch from "@/components/layout/GlobalSearch";
import NewDealLauncher from "@/components/deals/new-deal/NewDealLauncher";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import UserAvatar from "@/components/common/UserAvatar";
import { getTimeGreeting } from "@/utils/common.utils";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface HeaderTeammate {
  name?: string;
}

interface HeaderProps {
  onMenuClick?: () => void;
  teammates?: HeaderTeammate[];
  /** Override for the bell badge. Left unset, the badge reads the live unread
   *  count itself — which is what every call site does. */
  notificationCount?: number;
  subtitle?: string;
}

export default function Header({ onMenuClick, teammates = [], notificationCount, subtitle }: HeaderProps) {
  const { user, context, logout } = useAuth();
  const navigate = useNavigate();
  const [confirmLogout, setConfirmLogout] = React.useState(false);
  const isInternal = Boolean(context?.membership);
  // A client, lender or attorney has no brokerage. The old default said "across
  // your brokerage" to everyone, which was the one line on their whole portal
  // written for somebody else.
  const resolvedSubtitle = subtitle
    ?? (isInternal
      ? "Here is what needs attention across your brokerage."
      : "Here is where your deal stands today.");
  const unreadCount = useNotificationUnreadCount();
  const badgeCount = notificationCount ?? unreadCount;

  return (
    <header
      className="flex min-h-[72px] w-full flex-row flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-dash-border bg-dash-surface px-3 py-3 font-dashboard sm:h-[72px] sm:flex-nowrap sm:gap-4 sm:px-4 sm:py-0 lg:px-3"
      data-testid="dashboard-header"
    >
      <div className="flex min-w-0 items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onMenuClick}
          className="shrink-0 text-dash-muted lg:hidden"
          aria-label="Open menu"
          data-testid="dashboard-header-menu-btn"
        >
          <Menu />
        </Button>
        <div className="hidden min-w-[245px] 2xl:block">
          <h1 className="truncate text-xl font-bold text-dash-ink" data-testid="dashboard-header-greeting">
            {getTimeGreeting()}{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
          </h1>
          <p className="truncate text-xs text-dash-faint">{resolvedSubtitle}</p>
        </div>
      </div>

      <GlobalSearch className="order-3 w-full basis-full sm:order-none sm:max-w-[305px] sm:basis-auto sm:flex-1" />

      <div className="ml-auto flex shrink-0 flex-nowrap items-center justify-end gap-1 sm:gap-2">
        {teammates.length > 0 && (
          <div className="hidden items-center -space-x-2 md:flex">
            {teammates.slice(0, 5).map((t, i) => (
              <UserAvatar
                key={i}
                name={t.name}
                colored
                style={{ "--avatar-delay": `${i * 80}ms` } as React.CSSProperties}
                className="size-7 animate-avatar-team border-2 border-white transition-transform duration-200 hover:z-10 hover:-translate-y-0.5 hover:scale-110"
                fallbackClassName="text-[9px] font-bold"
              />
            ))}
            {teammates.length > 5 && (
              <Avatar
                style={{ "--avatar-delay": `${5 * 80}ms` } as React.CSSProperties}
                className="size-7 animate-avatar-team border-2 border-white transition-transform duration-200 hover:z-10 hover:-translate-y-0.5 hover:scale-110"
              >
                <AvatarFallback className="bg-dash-chip text-[9px] font-semibold text-dash-muted">
                  +{teammates.length - 5}
                </AvatarFallback>
              </Avatar>
            )}
          </div>
        )}

        {isInternal && (
          <NewDealLauncher
            size="icon"
            className="size-9 shrink-0 !rounded-full bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground hover:brightness-105"
            testId="dashboard-header-quick-add"
            ariaLabel="Quick add"
          >
            {""}
          </NewDealLauncher>
        )}

        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => navigate("/notifications")}
          className="relative size-9 shrink-0 rounded-full border border-dash-border bg-white text-dash-muted hover:bg-dash-hover hover:text-dash-ink"
          aria-label="Notifications"
          data-testid="dashboard-header-notifications"
        >
          <Bell />
          {badgeCount > 0 && (
            <span
              className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-dash-rust text-[8px] font-bold text-white"
              data-testid="dashboard-header-notifications-badge"
            >
              {badgeCount > 9 ? "9+" : badgeCount}
            </span>
          )}
        </Button>

        <Separator orientation="vertical" className="mx-1 hidden h-6 bg-dash-border sm:block" />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="seamless"
              type="button"
              className="flex items-center gap-2 rounded-xl p-1 hover:bg-dash-hover sm:py-1.5 sm:pl-1 sm:pr-2"
              data-testid="dashboard-header-user-menu"
            >
              <UserAvatar name={user?.name} src={user?.photo_url} className="size-9" fallbackClassName="text-xs font-bold" />
              <div className="hidden flex-col items-start leading-tight sm:flex">
                <span className="text-sm font-semibold text-dash-ink">{user?.name || "Account"}</span>
                <span className="text-[10px] text-dash-faint">
                  {isInternal ? (context?.membership?.role_label || "Brokerage") : "Guest"}
                </span>
              </div>
              <ChevronDown className="hidden size-3 text-dash-muted sm:block" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuGroup>
              <DropdownMenuItem
                onSelect={() => setConfirmLogout(true)}
                className="gap-2 text-sm"
                data-testid="dashboard-header-signout"
              >
                <LogOut className="size-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {confirmLogout && (
        <ConfirmDialog
          open
          onOpenChange={setConfirmLogout}
          title="Sign out?"
          description="You'll need to sign in again to get back into your workspace."
          confirmLabel="Sign out"
          tone="default"
          onConfirm={() => { logout(); navigate("/"); }}
          testId="logout-confirm-dialog"
        />
      )}
    </header>
  );
}
